import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { debounce } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { DateRange } from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WorkflowRunManualResumeOption, WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  clearManualRunResults,
  setCurrentPage as setRunsCurrentPage,
  setPageSize,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
} from 'dashboard/redux/actions/workflow-runs';
import {
  clearWorkflowVersions,
  setCurrentPage as setVersionsCurrentPage,
} from 'dashboard/redux/actions/workflow-versions';
import { ManualRunResults } from 'dashboard/redux/reducers/workflow-runs';
import { RootState } from 'dashboard/redux/store';
import {
  bulkResumeWorkflowRuns,
  createBulkManualRuns,
  loadWorkflowRunList,
} from 'dashboard/redux/thunk-actions/workflow-runs';
import { loadWorkflowVersionList } from 'dashboard/redux/thunk-actions/workflow-versions';
import DashboardWorkflowRunsView from 'dashboard/views/DashboardWorkflowRunsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { getDateRange, translateDateValuetoLuxon } from 'lib/utils/filters';

type DashboardWorkflowRunsRouteProps = PropsFromRedux;

const DashboardWorkflowRunsRoute = ({
  currentEnvironment,
  manualRunResults,
  runsCurrentPage,
  versionsCurrentPage,
  versionsTotalPages,
  workflowRuns,
  workflowRunFilters,
  workflowRunSortParams,
  workflowRunPaginationParams,
  workflowVersions,
  workflowsMap,
  pageSize,
  bulkResumeWorkflowRuns,
  clearManualRunResults,
  clearWorkflowVersions,
  createBulkManualRuns,
  loadWorkflowRunList,
  loadWorkflowVersionList,
  setPageSize,
  setRunsCurrentPage,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
  setVersionsCurrentPage,
  showMessage,
}: DashboardWorkflowRunsRouteProps) => {
  // When navigating from a specific workflow or routes such as case actions, we pass in the workflow explicitly via params
  const { workflowId: paramsWorkflowId } = useParams<{ workflowId?: string }>();

  // When given a specific workflow, we expose functionality such as creating manual runs
  // Gate this from the overall WorkflowRunsView when no parameter workflowId is passed (undefined)
  const [workflowId, setWorkflowId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (paramsWorkflowId) {
      setWorkflowRunFilters({ workflowIds: [paramsWorkflowId] });
      setWorkflowId(paramsWorkflowId);
    } else {
      setWorkflowRunFilters({ workflowIds: [] });
      setWorkflowId(undefined);
    }
  }, [paramsWorkflowId, setWorkflowRunFilters]);

  useOnMountAndUnmount(clearWorkflowVersions);
  const [selectedTableRows, setSelectedTableRows] = useState<Set<string>>(new Set());
  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    filters: workflowRunFilters,
    sortParams: workflowRunSortParams,
    pageSize: pageSize,
    currentPage: runsCurrentPage,
    setFilters: setWorkflowRunFilters,
    setSortParams: setWorkflowRunSortParams,
    setCurrentPage: setRunsCurrentPage,
    setPageSize: setPageSize,
  });

  const handleDateUpdate = useCallback(
    ({ startDate, endDate }: DateRange) => {
      if (startDate || endDate) {
        const { start, end } = getDateRange(startDate, endDate);
        setWorkflowRunFilters({ completedAtStart: start, completedAtEnd: end });
      } else {
        setWorkflowRunFilters({ completedAtStart: undefined, completedAtEnd: undefined });
      }
      setRunsCurrentPage(1);
    },
    [setWorkflowRunFilters, setRunsCurrentPage],
  );

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadWorkflowRunList,
      currentEnvironment,
      runsCurrentPage,
      pageSize,
      workflowRunFilters,
      workflowId,
      workflowRunSortParams,
    ],
  );

  const DEFAULT_FILTERS = { status: [], workflowIds: [], workflowVersionIds: [] };

  const { loading: isLoadingWorkflowRuns, callback: loadWorkflowRuns } = useApiLoader({
    loader: () =>
      loadWorkflowRunList({
        currentEnvironment,
        currentPage: runsCurrentPage,
        pageSize,
        filters: workflowRunFilters || DEFAULT_FILTERS,
        sortParams: workflowRunSortParams,
        abortSignal: abortController.signal,
      }),
    deps: [
      loadWorkflowRunList,
      currentEnvironment,
      runsCurrentPage,
      pageSize,
      workflowRunFilters,
      workflowRunSortParams,
    ],
    abortController,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load workflow runs.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { loading: isLoadingWorkflowVersions } = useApiLoader({
    loader: () =>
      loadWorkflowVersionList({
        workflowId: workflowId ?? null,
        currentPage: versionsCurrentPage,
        pageSize,
      }),
    deps: [loadWorkflowVersionList, versionsCurrentPage, pageSize, workflowId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load workflow versions.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) handleDateUpdate({ startDate, endDate });
      } else {
        handleDateUpdate({ startDate: undefined, endDate: undefined });
      }
    },
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = useCallback(
    (status: WorkflowRunStatus[]) => {
      setWorkflowRunFilters({ status });
      setRunsCurrentPage(1);
    },
    [setWorkflowRunFilters, setRunsCurrentPage],
  );

  const handleVersionFilterUpdate = useCallback(
    (workflowVersionIds: string[]) => {
      setWorkflowRunFilters({ workflowVersionIds });
      setRunsCurrentPage(1);
    },
    [setRunsCurrentPage, setWorkflowRunFilters],
  );

  const handleWorkflowIdFilterUpdate = useCallback(
    (workflowIds: string[]) => {
      setWorkflowRunFilters({ workflowIds });
      setRunsCurrentPage(1);
    },
    [setRunsCurrentPage, setWorkflowRunFilters],
  );

  const handleOmniFilterUpdate = useCallback(
    debounce(500, (omni: string) => {
      setWorkflowRunFilters({ omni });
      setRunsCurrentPage(1);
    }),
    [setRunsCurrentPage, setWorkflowRunFilters],
  );

  const onLoadMoreVersions =
    versionsCurrentPage < versionsTotalPages
      ? () => setVersionsCurrentPage(versionsCurrentPage + 1)
      : null;

  const {
    callback: handleBulkCreateManualWorkflowRuns,
    loading: isLoadingCreateBulkManualWorkflowRuns,
    error: createBulkManualRunsError,
  } = useApiCallback(
    async (objectIds: string[]) => {
      // TODO(PER-29004) allow from /dashboard/workflow-runs
      if (workflowId) {
        await createBulkManualRuns(workflowId, objectIds);
        await loadWorkflowRuns();
      }
    },
    [workflowId, loadWorkflowRuns, createBulkManualRuns],
  );
  useApiErrorHandler({
    error: createBulkManualRunsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to create manual runs, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('Failed to create manual runs, please try again', MessageType.Failure),
  });

  const {
    callback: handleBulkResumeWorkflowRuns,
    loading: isLoadingBulkResumeWorkflowRuns,
    error: bulkResumeManualRunsError,
  } = useApiCallback(
    async (objectIds: string[]) => {
      // TODO(PER-29004) allow from /dashboard/workflow-runs
      if (workflowId) {
        await bulkResumeWorkflowRuns(workflowId, objectIds);
        await loadWorkflowRuns();
        showMessage('Runs will trigger soon', MessageType.Success);
      }
    },
    [workflowId, loadWorkflowRuns, bulkResumeWorkflowRuns, showMessage],
  );
  useApiErrorHandler({
    error: bulkResumeManualRunsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to resume runs, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('Failed to resume runs, please try again', MessageType.Failure),
  });

  const handleResumeFormSubmit = useCallback(
    async (runType: string) => {
      if (runType === WorkflowRunManualResumeOption.Resume) {
        await handleBulkResumeWorkflowRuns([...selectedTableRows]);
      } else {
        await handleBulkCreateManualWorkflowRuns([...selectedTableRows]);
      }
      setSelectedTableRows(new Set());
      clearManualRunResults();
    },
    [
      selectedTableRows,
      setSelectedTableRows,
      handleBulkCreateManualWorkflowRuns,
      handleBulkResumeWorkflowRuns,
      clearManualRunResults,
    ],
  );

  return (
    <DashboardWorkflowRunsView
      clearManualRunResults={clearManualRunResults}
      manualRunResults={manualRunResults as ManualRunResults}
      workflowRuns={workflowRuns}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowRunFilters={workflowRunFilters}
      workflowId={workflowId ?? null}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowRunPaginationParams={workflowRunPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      workflowVersions={workflowVersions}
      workflowsMap={workflowsMap}
      isLoadingWorkflowVersions={isLoadingWorkflowVersions}
      isLoadingWorkflowRuns={isLoadingWorkflowRuns}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={workflowRunSortParams}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleVersionFilterUpdate={handleVersionFilterUpdate}
      handleWorkflowIdFilterUpdate={handleWorkflowIdFilterUpdate}
      handleSortUpdate={handleSortUpdate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onLoadMoreVersions={onLoadMoreVersions}
      handleOmniFilterUpdate={handleOmniFilterUpdate}
      isLoadingBulkResumeWorkflowRuns={isLoadingBulkResumeWorkflowRuns}
      isLoadingCreateBulkManualWorkflowRuns={isLoadingCreateBulkManualWorkflowRuns}
      handleBulkCreateManualWorkflowRuns={handleBulkCreateManualWorkflowRuns}
      handleResumeFormSubmit={handleResumeFormSubmit}
      showMessage={showMessage}
      selectedRows={selectedTableRows}
      // TODO(PER-29004) allow from /dashboard/workflow-runs
      setSelectedRows={workflowId ? setSelectedTableRows : undefined}
      currentEnvironment={currentEnvironment}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    workflowRuns:
      state.workflowRuns.workflowRuns[state.currentEnvironment.currentEnvironment.id] || [],
    workflowRunFilters: state.workflowRuns.filters,
    workflowRunSortParams: state.workflowRuns.sortParams,
    listsSortParams: state.lists.sortParams,
    workflowRunPaginationParams: state.workflowRuns.paginationParams,
    workflowsMap: state.workflowRuns.workflows,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    manualRunResults: state.workflowRuns.manualRunResults,
    runsCurrentPage: state.workflowRuns.currentPage,
    pageSize: state.workflowRuns.pageSize,
    workflowVersions: state.workflowVersions.workflowVersions,
    versionsCurrentPage: state.workflowVersions.currentPage,
    versionsTotalPages: state.workflowVersions.paginationParams?.['total-pages'],
  };
};

const mapDispatchToProps = {
  loadWorkflowRunList,
  loadWorkflowVersionList,
  clearWorkflowVersions,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
  setPageSize,
  setRunsCurrentPage,
  setVersionsCurrentPage,
  showMessage,
  createBulkManualRuns,
  clearManualRunResults,
  bulkResumeWorkflowRuns,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowRunsRoute);
